import { useMemo, useState } from "react";

import { useMediaQuery } from "@mui/material";
import { Visibility } from "@mui/icons-material";
import {
    BsMetaUtil,
    EarnPortfolioSummaryView,
    TokenListMetadata,
    useEarnPortfolioSummary,
    useLoopscaleWalletContext,
    useTokenListContext
} from "@bridgesplit/abf-react";
import {
    createRows,
    Icon,
    MEDIA,
    MobilePortfolioCards,
    MobilePortfolioPagination,
    MobilePortfolioRowConfig,
    Row,
    StatProps,
    TABLE_CTA_COLUMN_KEY,
    Table,
    TableColumn,
    TableKey,
    Text,
    UsdPriceTextWithTooltip
} from "@bridgesplit/ui";
import { ALL_VALUE, AllValueType, EMPTY_STRING, formatPercent, isAll, tokenAmountToUiNumber } from "@bridgesplit/utils";
import { COPY } from "app/constants";
import { useChunkedPaginationController, useClampPaginationOnCountChange } from "app/api/shared";
import { AppDialog, useAppDialog } from "app/utils";
import { buildEarnVaultIconMetadata } from "app/utils/domain/earn";
import { useNavigate } from "react-router-dom";
import { RateDisplay } from "app/components/points/app/common";

import { TableCardWrapper, getEarnVaultPath } from "../common";
import {
    EarnPortfolioPositionView,
    useEarnPortfolioPositionsView,
    useEarnPositionsPaginated
} from "./useEarnPortfolioPositionsView";
import { PortfolioTableStats, RewardPoint } from "./stats/common";
import { SectionCollapse, SectionSkeleton } from "./PortfolioSections";
import {
    CELL_WIDTH_DEFAULTS,
    GROUP_CTA_CELL,
    GROUP_HEADER_CELL_TOOLTIPS,
    GroupNameCell,
    PairTokenValueCell,
    RowActionsIconButton,
    TableCell
} from "./common";
import { ChipStatPair, PortfolioAllChip, PortfolioTokenChip } from "./mobileCards";
import { EarnVaultIcon } from "../explore/earn/EarnVaultIcon";

const PAGE_SIZE = 5;

const EARN_ROW_CONFIG: MobilePortfolioRowConfig = {
    headerDataIndex: "name",
    primaryValueDataIndex: "positionValue",
    statDataIndexes: ["unrealizedPnl", "apy"]
};

interface EarnSectionProps {
    isExpanded: boolean;
    isUsdValueLoading: boolean;
}

export default function EarnSection({ isExpanded, isUsdValueLoading }: EarnSectionProps) {
    const isMobile = useMediaQuery(MEDIA.SM.below);
    const {
        data: { userWalletAddress: wallet }
    } = useLoopscaleWalletContext();

    const { data: groups, isLoading } = useEarnPortfolioSummary(wallet);
    const { getMetadata } = useTokenListContext();

    // Detail rows reuse the per-vault portfolio view; the summary endpoint only carries the
    // grouped rollup (value, weighted APY, PnL) — not the per-vault breakdown each group expands to.
    const earnPortfolio = useEarnPortfolioPositionsView();
    const positionsByMint = useMemo(() => {
        const map = new Map<string, EarnPortfolioPositionView[]>();
        earnPortfolio.positions?.forEach((position) => {
            const mint = position.response.vault.acceptedAssetMint;
            const existing = map.get(mint);
            if (existing) existing.push(position);
            else map.set(mint, [position]);
        });
        return map;
    }, [earnPortfolio.positions]);
    const apyByMint = useMemo(() => {
        const map = new Map<string, number | undefined>();
        positionsByMint.forEach((positions, mint) => {
            map.set(mint, getWeightedEarnHeadlineApy(positions));
        });
        return map;
    }, [positionsByMint]);

    const tableRows = useMemo(
        () => createRows<EarnPortfolioSummaryView>(groups ?? [], (group) => group.depositMint),
        [groups]
    );

    const groupedColumnDefs = useMemo(() => getGroupColumnDefs(getMetadata, apyByMint), [apyByMint, getMetadata]);
    const itemColumnDefs = useMemo(() => getPositionDetailColumnDefs(getMetadata), [getMetadata]);

    const mobile = useMobileEarnView({ isMobile, groups, apyByMint, getMetadata });

    if (isUsdValueLoading) return <SectionSkeleton />;

    return (
        <SectionCollapse isExpanded={isExpanded}>
            <EarnPortfolioStats earnPortfolio={earnPortfolio} />
            <TableCardWrapper flushOnMobile>
                {isMobile ? (
                    <MobilePortfolioCards<EarnPortfolioSummaryView, EarnPortfolioPositionView>
                        filterLabel="Filter by asset"
                        activeFilter={mobile.activeFilter}
                        onFilterChange={mobile.onFilterChange}
                        groups={mobile.chipRows}
                        renderChip={(row) => (
                            <EarnChipContent group={row?.data} apyByMint={apyByMint} getMetadata={getMetadata} />
                        )}
                        groupsLoading={isLoading}
                        positions={mobile.positionRows}
                        positionColumns={itemColumnDefs}
                        rowConfig={EARN_ROW_CONFIG}
                        onPositionNavigatePath={(position) =>
                            `${getEarnVaultPath(position.response.vault)}?tab=position`
                        }
                        positionsLoading={mobile.positionsLoading}
                        pagination={mobile.pagination}
                        hasHealth={false}
                        hasSelect={false}
                        emptyPlaceholder={`No active ${COPY.EARN_TERM.toLowerCase()} positions`}
                        listLabel={mobile.listLabel}
                        allCard={
                            <PortfolioAllChip
                                loading={!mobile.aggregate}
                                count={mobile.aggregate?.count}
                                valueUsd={mobile.aggregate?.valueUsd}
                                assetCount={mobile.allGroupsTotal}
                                stats={
                                    <ChipStatPair
                                        loading={!mobile.aggregate}
                                        left={
                                            <PairTokenValueCell
                                                usdAmount={mobile.aggregate?.pnlUsd}
                                                primarySignDisplay
                                            />
                                        }
                                        right={<EarnApy apy={mobile.aggregate?.apy} />}
                                    />
                                }
                            />
                        }
                    />
                ) : (
                    <Table<EarnPortfolioSummaryView, EarnPortfolioPositionView>
                        rows={tableRows}
                        columns={groupedColumnDefs}
                        grouping={{
                            columns: itemColumnDefs,
                            getItems: (groupKey: TableKey) =>
                                typeof groupKey === "string" ? positionsByMint.get(groupKey) : undefined,
                            getDetailKey: (position) => position.response.vault.address,
                            isDetailsLoading: earnPortfolio.isLoading,
                            onDetailRowNavigatePath: (position) =>
                                `${getEarnVaultPath(position.response.vault)}?tab=position`
                        }}
                        paginationProps={{ type: "managed", pageSize: PAGE_SIZE }}
                        loading={isLoading}
                        emptyPlaceholder={`No active ${COPY.EARN_TERM.toLowerCase()} positions`}
                        showViewToggle
                        viewToggleStorageKey="PORTFOLIO_EARN_VIEW"
                    />
                )}
            </TableCardWrapper>
        </SectionCollapse>
    );
}

interface EarnAggregate {
    count: number;
    valueUsd: number | undefined;
    pnlUsd: number | undefined;
    apy: number | undefined;
}

function useMobileEarnView({
    isMobile,
    groups,
    apyByMint,
    getMetadata
}: {
    isMobile: boolean;
    groups: EarnPortfolioSummaryView[] | undefined;
    apyByMint: Map<string, number | undefined>;
    getMetadata: (mint: string | undefined | null) => TokenListMetadata | undefined;
}) {
    const [activeFilter, setActiveFilter] = useState<AllValueType | TableKey>(ALL_VALUE);

    const positionsPagination = useChunkedPaginationController<
        EarnPortfolioPositionView,
        { page: number; pageSize: number }
    >({}, PAGE_SIZE);
    const {
        positions: positionsChunk,
        totalCount: positionsTotal,
        isLoading: positionsLoading
    } = useEarnPositionsPaginated({
        pagination: positionsPagination.backendPagination,
        depositGroupKey: activeFilter,
        skip: !isMobile
    });
    useClampPaginationOnCountChange(positionsPagination.clampPageToCount, positionsTotal);

    const visiblePositions = useMemo(
        () => positionsPagination.visibleData(positionsChunk),
        [positionsChunk, positionsPagination]
    );

    const chipRows = useMemo(
        () => createRows<EarnPortfolioSummaryView>(groups ?? [], (group) => group.depositMint),
        [groups]
    );
    const positionRows = useMemo(
        () =>
            visiblePositions
                ? createRows<EarnPortfolioPositionView>(visiblePositions, (p) => p.response.vault.address)
                : undefined,
        [visiblePositions]
    );

    const onFilterChange = (next: AllValueType | TableKey) => {
        setActiveFilter(next);
        positionsPagination.resetPagination();
    };

    const activeGroupSymbol = useMemo(() => {
        if (isAll(activeFilter)) return undefined;
        const group = groups?.find((g) => g.depositMint === activeFilter);
        const metadata = group ? getMetadata(group.depositMint) : undefined;
        return metadata ? BsMetaUtil.getSymbol(metadata) : undefined;
    }, [activeFilter, groups, getMetadata]);
    const listLabel = `${activeGroupSymbol ?? "All"} positions`;

    const aggregate = useMemo((): EarnAggregate | undefined => {
        if (!groups) return undefined;
        const valueUsd = groups.reduce((sum, g) => sum + g.positionValueUsd, 0);
        const pnlUsd = groups.reduce((sum, g) => sum + (g.unrealizedPnlUsd ?? 0), 0);
        const count = groups.reduce((sum, g) => sum + g.count, 0);
        const apyResolved = groups.every((g) => apyByMint.get(g.depositMint) !== undefined);
        const apy =
            valueUsd > 0 && apyResolved
                ? groups.reduce(
                      (sum, g) => sum + (g.positionValueUsd / valueUsd) * (apyByMint.get(g.depositMint) ?? 0),
                      0
                  )
                : undefined;
        return { count, valueUsd, pnlUsd, apy };
    }, [groups, apyByMint]);

    const pagination: MobilePortfolioPagination = {
        page: positionsPagination.page,
        setPage: positionsPagination.setPage,
        pageSize: positionsPagination.pageSize,
        totalCount: positionsTotal
    };

    return {
        activeFilter,
        onFilterChange,
        chipRows,
        allGroupsTotal: groups?.length,
        aggregate,
        positionRows,
        positionsLoading,
        pagination,
        listLabel
    };
}

function EarnChipContent({
    group,
    apyByMint,
    getMetadata
}: {
    group: EarnPortfolioSummaryView | undefined;
    apyByMint: Map<string, number | undefined>;
    getMetadata: (mint: string | undefined | null) => TokenListMetadata | undefined;
}) {
    return (
        <PortfolioTokenChip
            metadata={getMetadata(group?.depositMint)}
            count={group?.count}
            valueUsd={group?.positionValueUsd}
            tokenAmount={group?.positionValue ? Number(group.positionValue.amount) : undefined}
            stats={
                <ChipStatPair
                    loading={!group}
                    left={<PairTokenValueCell usdAmount={group?.unrealizedPnlUsd} primarySignDisplay />}
                    right={<EarnApy apy={group ? apyByMint.get(group.depositMint) : undefined} />}
                />
            }
        />
    );
}

function EarnApy({ apy }: { apy: number | undefined }) {
    return <RateDisplay apy={apy} variant="body1" color="body" isFeatured underline={false} />;
}

function EarnPortfolioStats({ earnPortfolio }: { earnPortfolio: ReturnType<typeof useEarnPortfolioPositionsView> }) {
    const loading = earnPortfolio.isLoading;
    const totalDepositedValueUsd =
        earnPortfolio.totalValueUsd != null || earnPortfolio.fulfilledUnclaimedUsd != null
            ? (earnPortfolio.totalValueUsd ?? 0) + (earnPortfolio.fulfilledUnclaimedUsd ?? 0)
            : undefined;

    // Standardized section-stats order: Total value → APY → Daily interest → Daily rewards.
    const stats: StatProps[] = [
        {
            caption: COPY.TOTAL_SUPPLIED_TERM,
            value: <UsdPriceTextWithTooltip textColor="body" rawUsdValue={totalDepositedValueUsd} loading={loading} />
        },
        {
            caption: COPY.APY_TERM,
            value: formatPercent(earnPortfolio.totalApy)
        },
        {
            caption: COPY.DAILY_INTEREST_TERM,
            value: <PairTokenValueCell usdAmount={earnPortfolio.expectedDailyYieldUsd} />
        },
        {
            caption: COPY.DAILY_REWARDS_TERM,
            value: <RewardPoint points={earnPortfolio.totalDailyPoints ?? 0} variant="body1" />
        }
    ];

    return <PortfolioTableStats stats={stats} loading={loading} />;
}

function getGroupColumnDefs(
    getMetadata: (mint: string | undefined | null) => TokenListMetadata | undefined,
    apyByMint: Map<string, number | undefined>
): TableColumn<EarnPortfolioSummaryView>[] {
    return [
        {
            dataIndex: "position",
            title: "Position",
            render: (row) => <GroupNameCell metadata={getMetadata(row.data.depositMint)} count={row.data.count} />
        },
        {
            dataIndex: "positionValue",
            title: "Position value",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.pairValue,
            render: (row) => (
                <PairTokenValueCell
                    usdAmount={row.data.positionValueUsd}
                    tokenAmount={tokenAmountToUiNumber(row.data.positionValue)}
                    metadata={getMetadata(row.data.depositMint)}
                />
            )
        },
        {
            dataIndex: "unrealizedPnl",
            title: "Unrealized PnL",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.pairValue,
            render: (row) => <PairTokenValueCell usdAmount={row.data.unrealizedPnlUsd} primarySignDisplay />
        },
        {
            dataIndex: "apy",
            title: COPY.APY_TERM,
            tooltip: GROUP_HEADER_CELL_TOOLTIPS.vaults.apy,
            align: "right",
            width: CELL_WIDTH_DEFAULTS.apy,
            render: (row) => formatPercent(apyByMint.get(row.data.depositMint)),
            sortBy: ({ data }) => apyByMint.get(data.depositMint) ?? 0
        },
        {
            dataIndex: TABLE_CTA_COLUMN_KEY,
            title: EMPTY_STRING,
            align: "center",
            width: CELL_WIDTH_DEFAULTS.cta,
            render: GROUP_CTA_CELL
        }
    ];
}

function getWeightedEarnHeadlineApy(positions: EarnPortfolioPositionView[]): number | undefined {
    const weights = positions.map((position) => tokenAmountToUiNumber(position.assets) ?? 0);
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
    if (totalWeight <= 0) return undefined;
    if (positions.some((position) => position.headlineApy === undefined)) return undefined;

    return positions.reduce((sum, position, index) => {
        const weight = weights[index] / totalWeight;
        return sum + weight * (position.headlineApy ?? 0);
    }, 0);
}

function getPositionDetailColumnDefs(
    getMetadata: (mint: string | undefined | null) => TokenListMetadata | undefined
): TableColumn<EarnPortfolioPositionView>[] {
    return [
        {
            dataIndex: "name",
            title: "Position",
            render: (row) => (
                <NameCell
                    position={row.data}
                    depositMetadata={getMetadata(row.data.response.vault.acceptedAssetMint)}
                    iconMetadata={buildEarnVaultIconMetadata(row.data.response.vault, getMetadata)}
                />
            )
        },
        {
            dataIndex: "positionValue",
            title: "Position value",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.pairValue,
            render: (row) => (
                <PairTokenValueCell
                    usdAmount={row.data.valueUsd}
                    tokenAmount={tokenAmountToUiNumber(row.data.assets)}
                    metadata={getMetadata(row.data.response.vault.acceptedAssetMint)}
                />
            )
        },
        {
            dataIndex: "unrealizedPnl",
            title: "Unrealized PnL",
            align: "right",
            width: CELL_WIDTH_DEFAULTS.pairValue,
            render: (row) => <PairTokenValueCell usdAmount={row.data.unrealizedPnlUsd} primarySignDisplay />
        },
        {
            dataIndex: "apy",
            title: COPY.APY_TERM,
            align: "right",
            width: CELL_WIDTH_DEFAULTS.apy,
            render: (row) => <TableCell value={formatPercent(row.data.headlineApy)} />
        },
        {
            dataIndex: TABLE_CTA_COLUMN_KEY,
            title: EMPTY_STRING,
            align: "center",
            width: CELL_WIDTH_DEFAULTS.cta,
            render: (row) => <PositionCtaCell position={row.data} />
        }
    ];
}

/* Position cells */
function NameCell({
    position,
    depositMetadata,
    iconMetadata
}: {
    position: EarnPortfolioPositionView;
    depositMetadata: TokenListMetadata | undefined;
    iconMetadata: TokenListMetadata[] | undefined;
}) {
    return (
        <Row spacing={1} alignItems="center" justifyContent="center">
            <EarnVaultIcon metadata={iconMetadata} size={20} />
            <Text>{depositMetadata?.symbol}</Text>
            <Text variant="body2" color="caption">
                {position.response.vault.metadataJson.name}
            </Text>
        </Row>
    );
}

function PositionCtaCell({ position }: { position: EarnPortfolioPositionView }) {
    const { open } = useAppDialog();
    const navigate = useNavigate();
    const { vault } = position.response;

    const items = useMemo(
        () => [
            {
                text: "Deposit",
                icon: <Icon type="arrow-down" />,
                onClick: () => open(AppDialog.EarnDeposit, { vault })
            },
            {
                text: "Withdraw",
                icon: <Icon type="arrow-diagonal" />,
                onClick: () => open(AppDialog.EarnWithdraw, { vault })
            },
            {
                text: "View vault",
                icon: <Visibility />,
                onClick: () => navigate(`${getEarnVaultPath(vault)}?tab=position`)
            }
        ],
        [navigate, open, vault]
    );

    return <RowActionsIconButton items={items} />;
}
